// Map-related constants. Single source of truth for:
//   - MILES_TO_METERS conversion (used by Leaflet radius math + Turf.js inputs)
//   - AMENITY_CATEGORIES locked metadata (color, slug, label, singular, plural)
//
// The category metadata mirrors the v1.0 reference at web/public/map.js
// CATEGORIES array (LOCKED per Phase 03 D-01..D-04). Phase 08-UI-SPEC § 7.2
// re-locks the color hex values to the Glass Coast token palette:
//   dog_park   -> moss-500   (#6b8f5a)
//   beach      -> tide-400   (#7faec9)
//   daycare    -> bronze-500 (#c08850)  <- Phase 08 reserved-list extension §7.1
//   bar        -> coral-500  (#d96454)
//
// The order of AMENITY_CATEGORIES is the locked rendering order for legend
// rows + filter chips: parks -> beaches -> daycare -> bars.

export const MILES_TO_METERS = 1609.344;

export type AmenityCategory = 'dog_park' | 'beach' | 'daycare' | 'bar';

export interface AmenityCategoryMeta {
  db: AmenityCategory;
  slug: 'dog-park' | 'beach' | 'daycare' | 'bar';
  color: string;     // Glass Coast hex
  label: string;     // "Dog Parks" — used in legend + filter chip
  singular: string;  // "dog park"
  plural: string;    // "dog parks"
  displayName: string; // "Dog Park" — used in popup category badge
}

export const AMENITY_CATEGORIES: readonly AmenityCategoryMeta[] = [
  { db: 'dog_park', slug: 'dog-park', color: '#6b8f5a',
    label: 'Dog Parks', singular: 'dog park', plural: 'dog parks',
    displayName: 'Dog Park' },
  { db: 'beach', slug: 'beach', color: '#7faec9',
    label: 'Dog Beaches', singular: 'dog beach', plural: 'dog beaches',
    displayName: 'Dog Beach' },
  { db: 'daycare', slug: 'daycare', color: '#c08850',
    label: 'Daycare', singular: 'daycare', plural: 'daycares',
    displayName: 'Daycare' },
  { db: 'bar', slug: 'bar', color: '#d96454',
    label: 'Dog-Friendly Bars', singular: 'dog-friendly bar', plural: 'dog-friendly bars',
    displayName: 'Dog-Friendly Bar' },
] as const;

/** Lookup map: category DB value -> metadata. */
export const AMENITY_CATEGORY_BY_DB: Record<AmenityCategory, AmenityCategoryMeta> =
  AMENITY_CATEGORIES.reduce((acc, c) => {
    acc[c.db] = c;
    return acc;
  }, {} as Record<AmenityCategory, AmenityCategoryMeta>);

/** Property marker colors (UI-SPEC § 7.3 locked).
 *  Phase 08-r — DEFAULT lightened from ocean-800 (#152740) to tide-400
 *  (#7faec9) so out-of-radius markers are visible on the dark CartoDB tile
 *  layer. The original near-black hex was meant for the bright OSM tiles. */
export const PROPERTY_COLOR_DEFAULT = '#7faec9'; // tide-400
export const PROPERTY_COLOR_IN_RADIUS = '#6b8f5a'; // moss-500
export const PROPERTY_STROKE = '#f4f7f9'; // foam-50
